import { useMemo, useState, type FormEvent } from 'react';
import {
  Mail,
  MoreHorizontal,
  Trash2,
  UserMinus,
  UserPlus,
  Zap,
} from 'lucide-react';
import { useCurrentUserId } from '@repo/api-client/react';
import type { Invite, Membership } from '@repo/api-client/dtos';
import {
  useCreateInvite,
  useInvites,
  useMemberships,
  useRemoveMember,
  useRevokeInvite,
  useTransferOwnership,
  useUpdateMembershipRole,
} from '@repo/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { useActiveRole } from '@/features/auth/useActiveRole';

type ConfirmState =
  | { kind: 'idle' }
  | { kind: 'remove'; member: Membership }
  | { kind: 'revoke'; invite: Invite }
  | { kind: 'transfer'; member: Membership };

interface TeamManagementPanelProps {
  orgId: string;
  orgName: string;
}

/**
 * Shared team UI for `/settings/team` and the Team tab on business detail.
 * Mirrors mobile's role matrix: owner/admin manage; members see a guard.
 */
export function TeamManagementPanel({ orgId, orgName }: TeamManagementPanelProps) {
  const userId = useCurrentUserId();
  const { canManageTeam, canArchiveOrg } = useActiveRole();
  const { memberships, loading: loadingMembers } = useMemberships(orgId);
  const { invites, loading: loadingInvites } = useInvites(orgId);
  const { createInvite, loading: inviting } = useCreateInvite();
  const { revokeInvite, loading: revoking } = useRevokeInvite();
  const { removeMember, loading: removing } = useRemoveMember();
  const { updateRole, loading: updatingRole } = useUpdateMembershipRole();
  const { transferOwnership, loading: transferring } = useTransferOwnership();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [confirm, setConfirm] = useState<ConfirmState>({ kind: 'idle' });

  const memberRows = useMemo(
    () =>
      (memberships ?? []).map((m) => ({
        ...m,
        isCurrentUser: !!userId && m.userId === userId,
      })),
    [memberships, userId],
  );

  if (!canManageTeam) {
    return (
      <Card>
        <CardContent className="py-10">
          <EmptyState
            icon={<UserMinus className="size-7" />}
            title="Team is owner & admin only"
            description="Ask your workspace owner or an admin to add or remove people from this workspace."
          />
        </CardContent>
      </Card>
    );
  }

  async function onInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await createInvite({ orgId, email: email.trim().toLowerCase(), role: inviteRole });
      setEmail('');
      toast({ title: 'Invite sent', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function handleConfirm() {
    if (confirm.kind === 'remove') {
      try {
        await removeMember({ orgId, userId: confirm.member.userId });
        setConfirm({ kind: 'idle' });
        toast({ title: 'Member removed', variant: 'success' });
      } catch (err) {
        const desc = describeAppError(err);
        toast({ title: desc.title, description: desc.message, variant: 'danger' });
      }
    } else if (confirm.kind === 'revoke') {
      try {
        await revokeInvite({ orgId, inviteId: confirm.invite.id });
        setConfirm({ kind: 'idle' });
        toast({ title: 'Invite revoked', variant: 'success' });
      } catch (err) {
        const desc = describeAppError(err);
        toast({ title: desc.title, description: desc.message, variant: 'danger' });
      }
    } else if (confirm.kind === 'transfer') {
      try {
        await transferOwnership({ orgId, userId: confirm.member.userId });
        setConfirm({ kind: 'idle' });
        toast({ title: 'Ownership transferred', variant: 'success' });
      } catch (err) {
        const desc = describeAppError(err);
        toast({ title: desc.title, description: desc.message, variant: 'danger' });
      }
    }
  }

  const pendingInvites = (invites ?? []).filter((i) => !i.acceptedAt && !i.revokedAt);

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Members</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {loadingMembers ? (
              <Skeleton className="h-24 rounded-md" />
            ) : memberRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members yet.</p>
            ) : (
              memberRows.map((m) => (
                <MemberRow
                  key={`${m.orgId}-${m.userId}`}
                  member={m}
                  canArchiveOrg={canArchiveOrg}
                  busy={removing || updatingRole || transferring}
                  onRemove={() => setConfirm({ kind: 'remove', member: m })}
                  onTransfer={() => setConfirm({ kind: 'transfer', member: m })}
                  onPromote={async () => {
                    try {
                      await updateRole({ orgId, userId: m.userId, role: 'admin' });
                      toast({ title: 'Promoted to admin', variant: 'success' });
                    } catch (err) {
                      const desc = describeAppError(err);
                      toast({
                        title: desc.title,
                        description: desc.message,
                        variant: 'danger',
                      });
                    }
                  }}
                  onDemote={async () => {
                    try {
                      await updateRole({ orgId, userId: m.userId, role: 'member' });
                      toast({ title: 'Demoted to member', variant: 'success' });
                    } catch (err) {
                      const desc = describeAppError(err);
                      toast({
                        title: desc.title,
                        description: desc.message,
                        variant: 'danger',
                      });
                    }
                  }}
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invite teammate</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onInvite} className="flex flex-col gap-3">
              <Field label="Email" htmlFor={`inviteEmail-${orgId}`} required>
                <Input
                  id={`inviteEmail-${orgId}`}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field label="Role" htmlFor={`inviteRole-${orgId}`}>
                <Select
                  value={inviteRole}
                  onValueChange={(v) => setInviteRole(v as 'admin' | 'member')}
                >
                  <SelectTrigger id={`inviteRole-${orgId}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Button type="submit" loading={inviting}>
                <UserPlus className="size-4" /> Send invite
              </Button>
            </form>

            <div className="mt-6">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Pending invites
              </h4>
              {loadingInvites ? (
                <Skeleton className="h-10 rounded-md" />
              ) : pendingInvites.length === 0 ? (
                <p className="text-sm text-muted-foreground">None pending.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {pendingInvites.map((invite) => (
                    <li
                      key={invite.id}
                      className="flex items-center justify-between rounded-md border border-line p-2"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="size-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm">{invite.email}</div>
                          <div className="text-xs capitalize text-muted-foreground">
                            {invite.role}
                            {invite.expiresAt
                              ? ` · expires ${formatDate(invite.expiresAt)}`
                              : null}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirm({ kind: 'revoke', invite })}
                      >
                        <Trash2 className="size-4 text-destructive" /> Revoke
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        confirm={confirm}
        orgName={orgName}
        revoking={revoking}
        removing={removing}
        transferring={transferring}
        onClose={() => setConfirm({ kind: 'idle' })}
        onConfirm={handleConfirm}
      />
    </>
  );
}

function MemberRow({
  member,
  canArchiveOrg,
  busy,
  onRemove,
  onTransfer,
  onPromote,
  onDemote,
}: {
  member: Membership & { isCurrentUser: boolean };
  canArchiveOrg: boolean;
  busy: boolean;
  onRemove: () => void;
  onTransfer: () => void;
  onPromote: () => void;
  onDemote: () => void;
}) {
  const display =
    [member.firstName, member.lastName].filter(Boolean).join(' ') ||
    member.email ||
    member.userId;
  const isOwnerTarget = member.role === 'owner';
  const showMenu =
    !member.isCurrentUser && (!isOwnerTarget || canArchiveOrg);

  return (
    <div className="flex items-center justify-between rounded-md border border-line p-3">
      <div>
        <div className="text-sm font-medium text-foreground">
          {display}
          {member.isCurrentUser ? (
            <span className="text-muted-foreground"> (you)</span>
          ) : null}
        </div>
        {member.email ? (
          <div className="text-xs text-muted-foreground">{member.email}</div>
        ) : null}
        {member.joinedAt ? (
          <div className="text-xs text-muted-foreground">
            Joined {formatDate(member.joinedAt)}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={member.role === 'owner' ? 'accent' : 'default'} className="capitalize">
          {member.role}
        </Badge>
        {showMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`Actions for ${display}`} disabled={busy}>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isOwnerTarget && member.role !== 'admin' ? (
                <DropdownMenuItem onSelect={onPromote}>Promote to admin</DropdownMenuItem>
              ) : null}
              {!isOwnerTarget && member.role !== 'member' ? (
                <DropdownMenuItem onSelect={onDemote}>Demote to member</DropdownMenuItem>
              ) : null}
              {canArchiveOrg && !isOwnerTarget ? (
                <DropdownMenuItem onSelect={onTransfer}>
                  <Zap className="size-4" /> Transfer ownership
                </DropdownMenuItem>
              ) : null}
              {!isOwnerTarget ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={onRemove}
                    className="text-destructive focus:text-destructive"
                  >
                    <UserMinus className="size-4" /> Remove from workspace
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}

function ConfirmDialog({
  confirm,
  orgName,
  revoking,
  removing,
  transferring,
  onClose,
  onConfirm,
}: {
  confirm: ConfirmState;
  orgName: string;
  revoking: boolean;
  removing: boolean;
  transferring: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const open = confirm.kind !== 'idle';
  let title = '';
  let description = '';
  let confirmLabel = 'Confirm';
  let tone: 'default' | 'danger' = 'default';
  let loading = false;

  if (confirm.kind === 'remove') {
    title = 'Remove this member?';
    description = `${confirm.member.email ?? 'This person'} will lose access to ${orgName} immediately.`;
    confirmLabel = 'Remove';
    tone = 'danger';
    loading = removing;
  } else if (confirm.kind === 'revoke') {
    title = 'Cancel this invite?';
    description = `${confirm.invite.email} won't be able to use this link anymore.`;
    confirmLabel = 'Cancel invite';
    tone = 'danger';
    loading = revoking;
  } else if (confirm.kind === 'transfer') {
    title = 'Transfer ownership?';
    description = `${confirm.member.email ?? 'This person'} will become the workspace owner. You'll be demoted to admin.`;
    confirmLabel = 'Transfer ownership';
    tone = 'danger';
    loading = transferring;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
